import React, { useState, useEffect } from "react";
import { X, TrendingUp, TrendingDown, Minus, Globe, Target, FileText, Eye, ExternalLink, HelpCircle, Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import KeywordSparkline from "./KeywordSparkline";

/**
 * Parse a keyword string to extract the service part and the location part.
 * Examples:
 *   "best deck builders in westchester county ny" → { service: "best deck builders", location: "Westchester County, NY" }
 *   "kitchen renovation contractors long island ny" → { service: "kitchen renovation contractors", location: "Long Island, NY" }
 */
function parseKeywordLocation(keyword) {
  if (!keyword) return { service: keyword, location: "" };

  // Common location prepositions
  const prepositions = [" in ", " near ", " around ", " for ", " serving "];
  let service = keyword;
  let location = "";

  for (const prep of prepositions) {
    const idx = keyword.toLowerCase().indexOf(prep);
    if (idx > 0) {
      service = keyword.substring(0, idx);
      location = keyword.substring(idx + prep.length);
      break;
    }
  }

  // If no preposition found, check for US state abbreviations at the end
  if (!location) {
    const stateMatch = keyword.match(/^(.+?)\s+([\w\s]+?)\s+(ny|nj|ct|ca|tx|fl|pa|ma|il|oh|ga|nc|va|md|wa|co|az|or|mi|mn|wi|mo|tn|in|sc|al|la|ky|ok|ia|ar|ms|ut|nv|nm|ne|ks|wv|id|hi|me|nh|ri|mt|de|sd|nd|ak|vt|wy|dc)$/i);
    if (stateMatch) {
      // Check if the second group looks like a location (capitalize each word)
      const possibleLocation = stateMatch[2].trim();
      const locationWords = possibleLocation.split(/\s+/);
      // If there are 1-3 words and they look like a place name
      if (locationWords.length <= 4) {
        service = stateMatch[1].trim();
        location = possibleLocation + " " + stateMatch[3].toUpperCase();
      }
    }
  }

  // Capitalize location properly
  if (location) {
    location = location
      .split(/\s+/)
      .map((w) => {
        if (w.length <= 2) return w.toUpperCase(); // State abbreviations
        return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
      })
      .join(" ")
      // Fix common patterns
      .replace(/\bNy\b/g, "NY")
      .replace(/\bNj\b/g, "NJ")
      .replace(/\bCt\b/g, "CT")
      .replace(/\bCa\b/g, "CA")
      .replace(/\bTx\b/g, "TX")
      .replace(/\bFl\b/g, "FL");
  }

  return { service: service.trim(), location: location.trim() };
}

/**
 * Determine the best page type based on the keyword.
 */
function detectPageType(keyword) {
  const lower = keyword.toLowerCase();
  if (/\b(cost|price|pricing|how much|rates?|fees?|estimate)\b/.test(lower)) return "cost_guide";
  if (/\b(faq|questions?|ask)\b/.test(lower)) return "faq";
  // If has a location component, service_location
  const { location } = parseKeywordLocation(keyword);
  if (location) return "service_location";
  return "service";
}

/**
 * Build the /pages URL with pre-fill query params.
 */
function buildPageGeneratorUrl(keyword) {
  const { service, location } = parseKeywordLocation(keyword);
  const pageType = detectPageType(keyword);
  const params = new URLSearchParams();
  params.set("action", "generate");
  params.set("keyword", service);
  if (location) params.set("location", location);
  params.set("type", pageType);
  return `/pages?${params.toString()}`;
}

export { parseKeywordLocation, detectPageType, buildPageGeneratorUrl };

export default function KeywordDetailModal({ keyword, selectedSite, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [serpResults, setSerpResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paaData, setPaaData] = useState(null);

  useEffect(() => {
    if (!keyword || !selectedSite) return;
    setLoading(true);

    // Fetch keyword details
    api.get(`/websites/${selectedSite.id}/keywords/`, { params: { search: keyword } })
      .then((res) => {
        const results = res.data.results || res.data;
        const match = results.find((k) => k.keyword === keyword);
        setData(match || results[0] || null);
      })
      .catch(() => setData(null));

    // Fetch SERP competitors
    api.get(`/websites/${selectedSite.id}/keywords/serp-preview/`, { params: { keyword } })
      .then((res) => setSerpResults(res.data || []))
      .catch(() => setSerpResults([]))
      .finally(() => setLoading(false));

    // Check for PAA data
    api.get(`/websites/${selectedSite.id}/serp-features/`, { params: { feature_type: "people_also_ask" } })
      .then((res) => {
        const features = res.data.results || res.data;
        const match = features.find((f) => f.keyword === keyword);
        if (match && match.paa_questions?.length > 0) {
          setPaaData(match);
        }
      })
      .catch(() => {});
  }, [keyword, selectedSite]);

  if (!keyword) return null;

  const trendIcon = data?.trend === "rising" ? TrendingUp : data?.trend === "falling" ? TrendingDown : Minus;
  const TrendIcon = trendIcon;
  const trendColor = data?.trend === "rising" ? "text-green-400" : data?.trend === "falling" ? "text-red-400" : "text-gray-500";

  const handleCreatePage = () => {
    onClose();
    navigate(buildPageGeneratorUrl(keyword));
  };

  const handleGenerateFaq = () => {
    const { service, location } = parseKeywordLocation(keyword);
    const params = new URLSearchParams();
    params.set("action", "generate");
    params.set("keyword", service);
    if (location) params.set("location", location);
    params.set("type", "faq");
    if (paaData?.paa_questions) {
      params.set("paa_questions", JSON.stringify(paaData.paa_questions.map((q) => q.question || q)));
    }
    onClose();
    navigate(`/pages?${params.toString()}`);
  };

  const handleTrackKeyword = () => {
    if (!data) {
      api.post(`/websites/${selectedSite.id}/keywords/`, { keyword })
        .then(() => setData((prev) => ({ ...prev, keyword, tracked: true })))
        .catch(() => {});
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-dark-50">
          <div>
            <h3 className="text-base font-semibold font-mono">{keyword}</h3>
            {data?.target_url && <div className="text-xs text-gray-500 mt-0.5 truncate max-w-md">{data.target_url}</div>}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-dark-50/50 rounded-lg transition-colors">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="py-12 text-center text-gray-500 animate-pulse">Loading keyword data...</div>
          ) : (
            <>
              {/* Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-dark/50 rounded-lg p-3 text-center">
                  <div className="text-lg font-mono font-bold text-copper">
                    {data?.current_position ? `#${data.current_position.toFixed(1)}` : "—"}
                  </div>
                  <div className="text-xs text-gray-500">Position</div>
                </div>
                <div className="bg-dark/50 rounded-lg p-3 text-center">
                  <div className="text-lg font-mono font-bold">{data?.impressions?.toLocaleString() || "—"}</div>
                  <div className="text-xs text-gray-500">Impressions</div>
                </div>
                <div className="bg-dark/50 rounded-lg p-3 text-center">
                  <div className="text-lg font-mono font-bold">{data?.clicks?.toLocaleString() || "—"}</div>
                  <div className="text-xs text-gray-500">Clicks</div>
                </div>
                <div className="bg-dark/50 rounded-lg p-3 text-center">
                  <div className="text-lg font-mono font-bold">{data?.ctr ? `${(data.ctr * 100).toFixed(1)}%` : "—"}</div>
                  <div className="text-xs text-gray-500">CTR</div>
                </div>
              </div>

              {/* Trend */}
              {data && (
                <div className="flex items-center gap-3">
                  <TrendIcon size={16} className={trendColor} />
                  <span className={`text-sm ${trendColor}`}>
                    {data.trend === "rising" ? "Trending up" : data.trend === "falling" ? "Trending down" : "Stable"}
                    {data.position_change ? ` (${data.position_change > 0 ? "+" : ""}${data.position_change.toFixed(1)} positions)` : ""}
                  </span>
                  {data.history && data.history.length > 0 && (
                    <div className="ml-auto">
                      <KeywordSparkline data={data.history} width={120} height={30} />
                    </div>
                  )}
                </div>
              )}

              {/* Ranking Page */}
              {data?.target_url && (
                <div className="bg-dark/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Ranking Page</div>
                  <a href={data.target_url} target="_blank" rel="noopener noreferrer" className="text-sm text-copper hover:underline flex items-center gap-1">
                    {data.target_url.replace(/^https?:\/\//, "")}
                    <ExternalLink size={12} />
                  </a>
                </div>
              )}

              {/* SERP Competitors */}
              {serpResults.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 font-medium mb-2">Top SERP Competitors</div>
                  <div className="space-y-1.5">
                    {serpResults.slice(0, 10).map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-dark/30 rounded px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs font-mono text-gray-500 w-5 text-right">#{r.position || i + 1}</span>
                          <span className="text-gray-300 truncate max-w-xs">{r.title || r.domain}</span>
                        </div>
                        <span className="text-xs text-gray-500 font-mono truncate max-w-[150px]">{r.domain}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-dark-50">
                <button
                  onClick={handleCreatePage}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs bg-copper/20 text-copper rounded-lg hover:bg-copper/30 transition-colors"
                >
                  <FileText size={13} /> Create Landing Page
                </button>
                {paaData && (
                  <button
                    onClick={handleGenerateFaq}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs bg-purple-900/20 text-purple-400 rounded-lg hover:bg-purple-900/30 transition-colors"
                  >
                    <HelpCircle size={13} /> Generate FAQ
                  </button>
                )}
                <button
                  onClick={() => { onClose(); navigate(`/content/new?keyword=${encodeURIComponent(keyword)}`); }}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs bg-blue-900/20 text-blue-400 rounded-lg hover:bg-blue-900/30 transition-colors"
                >
                  <FileText size={13} /> Create Blog Post
                </button>
                <button
                  onClick={() => { onClose(); navigate(`/competitors?keyword=${encodeURIComponent(keyword)}`); }}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs bg-dark-50 text-gray-300 rounded-lg hover:bg-dark-50/80 transition-colors"
                >
                  <Target size={13} /> Analyze Competitors
                </button>
                {!data && (
                  <button
                    onClick={handleTrackKeyword}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs bg-green-900/20 text-green-400 rounded-lg hover:bg-green-900/30 transition-colors"
                  >
                    <Plus size={13} /> Track Keyword
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
